import React, { useEffect } from "react";
import { useWatch, useFieldArray } from "react-hook-form";
import { Button, Spinner, Collapse } from "reactstrap";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { validations } from "validations/FormValidations";
import AdverseEventStepper from "./AdverseEventStepper";

interface AdverseEventFollowupsFormProps {
  control: any;
  setValue: any;
  watch: any;
  getValues: any;
  errors: any;
  clearErrors: any;
  trigger?: any;
  onSubmit: (data: any) => void;
  onBack: () => void;
  onCancel: () => void;
  isPending?: boolean;
  isSaved: boolean;
  onNext: () => void;
  isEditMode?: boolean;
  onDeleteFollowup?: (id: number, index: number, onConfirm: () => void) => void;
  onSaveCard?: (index: number) => void;
  savingCardIndex?: number | null;
  fields?: any[];
  append?: any;
  remove?: any;
  isFollowupRequired?: string;
}

const AdverseEventFollowupsForm: React.FC<AdverseEventFollowupsFormProps> = ({
  control,
  setValue,
  getValues,
  errors,
  clearErrors,
  trigger,
  onSubmit,
  onBack,
  onNext,
  isEditMode = false,
  onDeleteFollowup,
  onSaveCard,
  savingCardIndex = null,
  fields: passedFields,
  append,
  remove,
  isFollowupRequired,
}) => {
  // Map nested array errors into flat keys matching field names dynamically on every render
  const flatErrors: Record<string, any> = {};
  if (errors?.followups) {
    Object.keys(errors.followups).forEach((indexKey) => {
      const index = Number(indexKey);
      const fu = (errors.followups as any)[indexKey];
      if (fu && typeof fu === "object") {
        Object.keys(fu).forEach((key) => {
          flatErrors[`followups.${index}.${key}`] = fu[key];
        });
      }
    });
  }

  // Use field array to manage multiple follow-ups
  const localFieldArray = useFieldArray({
    control,
    name: "followups",
  });

  const fields = passedFields || localFieldArray.fields;

  const [collapsedMap, setCollapsedMap] = React.useState<Record<string, boolean>>({});
  const watchedFollowups = useWatch({
    control,
    name: "followups",
  }) || [];

  // Temperature validation
  useEffect(() => {
    watchedFollowups.forEach((fu: any, index: number) => {
      const watchedUnit = fu?.temperatureunit;
      const watchedLocation = fu?.temperaturelocation;

      if (!watchedUnit) {
        if (fu?.temperaturelocation !== "" || fu?.temperature !== null) {
          setValue(`followups.${index}.temperaturelocation`, "");
          setValue(`followups.${index}.temperature`, null);
          clearErrors(`followups.${index}.temperaturelocation`);
          clearErrors(`followups.${index}.temperature`);
        }
      } else if (!watchedLocation) {
        if (fu?.temperature !== null) {
          setValue(`followups.${index}.temperature`, null);
          clearErrors(`followups.${index}.temperature`);
        }
      }
    });
  }, [watchedFollowups, setValue, clearErrors]);


  // Re-trigger validation on endtime when starttime changes
  useEffect(() => {
    watchedFollowups.forEach((fu: any, index: number) => {
      const start = fu?.starttime;
      const end = fu?.endtime;
      if (start && end) {
        trigger?.(`followups.${index}.endtime`);
      }
    });
  }, [watchedFollowups, trigger]);

  return (
    <form className={formStyles.formShell} onSubmit={onSubmit}>
      {!isEditMode && (
        <>
          {/* Page Header */}
          <div className="mb-4">
            <h4 className="mb-0 fw-700">Log New Adverse Event</h4>
          </div>

          <AdverseEventStepper activeTab="followups" />
        </>
      )}

      {isFollowupRequired === "no" || fields.length === 0 ? (
        <div className="card border-0 shadow-sm p-5 text-center mt-0 mb-4 flex-grow-1 d-flex flex-column align-items-center justify-content-center" style={{ borderRadius: "12px", border: "1px solid #f1f5f9" }}>
          <div className="d-flex align-items-center justify-content-center mb-3" style={{ width: "64px", height: "64px", borderRadius: "50%", backgroundColor: "rgba(6, 182, 212, 0.06)" }}>
            <i className="ri-information-line" style={{ fontSize: "32px", color: "#06b6d4" }}></i>
          </div>
          <h5 className="fw-semibold text-dark mb-2">Follow-up is not required</h5>
          <p className="text-muted small mx-auto mb-0" style={{ maxWidth: "420px" }}>
            You have selected "No" for Follow-up on the event details screen or no follow-up records exist.
          </p>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0 fw-semibold text-muted">Follow-up List</h5>
          </div>

          {/* List of Card-wise Follow-up forms */}
          {fields.map((field, index) => {
            const watchedTemperatureUnit = watchedFollowups[index]?.temperatureunit;
            const watchedTemperatureLocation = watchedFollowups[index]?.temperaturelocation;
            const currentFollowup = watchedFollowups[index] || {};
            const isCardSaved = !!currentFollowup.isSaved;

            const followupFields: FormFieldConfig[] = [
              {
                name: `followups.${index}.folloupdate`,
                label: "Follow-up Date",
                type: "date",
                required: true,
                optionsProps: index > 0
                  ? (() => {
                    const prevDate = watchedFollowups[index - 1]?.folloupdate;
                    if (!prevDate) return {};
                    // Create local Date from YYYY-MM-DD
                    const minDate = new Date(prevDate);
                    minDate.setDate(minDate.getDate() + 1);
                    return { minDate };
                  })()
                  : {},
              },
              {
                name: `followups.${index}.starttime`,
                label: "Start time",
                required: true,
                type: "time",
              },
              {
                name: `followups.${index}.endtime`,
                label: "End time",
                type: "time",
                rules: {
                  validate: (value: string) => {
                    if (!value) return true;
                    const start = getValues(`followups.${index}.starttime`);
                    if (!start) return true;

                    const [startH, startM] = start.split(":").map(Number);
                    const [endH, endM] = value.split(":").map(Number);

                    const startMinutes = startH * 60 + startM;
                    const endMinutes = endH * 60 + endM;

                    if (endMinutes < startMinutes) {
                      return "End time cannot be before start time";
                    }
                    return true;
                  },
                },
              },
              {
                name: `followups.${index}.bloodpressure`,
                label: "Blood pressure",
                type: "text",
                placeholder: "e.g. 120/80",
                rules: validations.adverseEvent.bloodpressure,
              },
              {
                name: `followups.${index}.pulse`,
                label: "Pulse (beats/min)",
                type: "number",
                placeholder: "e.g. 72",
                rules: validations.adverseEvent.pulse,
              },
              {
                name: `followups.${index}.respiratoryrate`,
                label: "Respiratory rate",
                type: "number",
                placeholder: "e.g. 15",
                rules: validations.adverseEvent.respiratoryrate,
              },
              {
                name: `followups.${index}.temperatureunit`,
                label: "Temperature Unit",
                type: "select",
                required: true,
                options: [
                  { label: "Celsius (°C)", value: "C" },
                  { label: "Fahrenheit (°F)", value: "F" },
                ],
                placeholder: "Select Unit...",
              },
              {
                name: `followups.${index}.temperaturelocation`,
                label: "Temperature Location",
                type: "select",
                required: !!watchedTemperatureUnit,
                disabled: !watchedTemperatureUnit,
                options: [
                  { label: "Oral", value: "Oral" },
                  { label: "Axillary", value: "Axillary" },
                ],
                placeholder: "Select Location...",
              },
              {
                name: `followups.${index}.temperature`,
                label: "Temperature Value",
                type: "number",
                placeholder: "e.g. 98.6",
                required: !!watchedTemperatureUnit && !!watchedTemperatureLocation,
                disabled: !watchedTemperatureUnit || !watchedTemperatureLocation,
                rules: (watchedTemperatureUnit && watchedTemperatureLocation)
                  ? (typeof validations.adverseEvent.temperature === "function"
                    ? validations.adverseEvent.temperature((key: string) => (getValues as any)(`followups.${index}.${key}`))
                    : validations.adverseEvent.temperature)
                  : undefined,
              },
              {
                name: `followups.${index}.description`,
                label: "Description",
                type: "textarea",
                placeholder: "Status at this check-in...",
                rules: {
                  validate: (value: string) => {
                    if (!value) return true;
                    if (value.length > 500) {
                      return "Description cannot exceed 500 characters";
                    }
                    return true;
                  },
                },
              },
              {
                name: `followups.${index}.nextfollowuprequired`,
                label: "Is another follow up required?",
                type: "radio",
                options: [
                  { label: "Yes", value: "yes" },
                  { label: "No", value: "no" },
                ],
              },
            ];

            const hasErrors = !!errors?.followups?.[index];
            const isCollapsed = hasErrors ? false : !!collapsedMap[field.id];

            return (
              <div className={formStyles.sectionCard} key={field.id}>
                <div
                  className={formStyles.sectionHeader}
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => {
                    setCollapsedMap((prev) => ({
                      ...prev,
                      [field.id]: !prev[field.id],
                    }));
                  }}
                >
                  <i className={`ri-file-list-3-line ${formStyles.sectionIcon}`} />
                  <div className={formStyles.sectionTitleWrap}>
                    <p className={formStyles.sectionTitle}>{`Follow-up ${String(index + 1).padStart(2, "0")}`}</p>
                  </div>
                  <div
                    className="d-flex align-items-center gap-3 ms-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isEditMode && index === fields.length - 1 && (
                      <i
                        className="ri-delete-bin-line text-danger"
                        style={{ fontSize: "18px", cursor: "pointer" }}
                        onClick={() => {
                          const fId = getValues(`followups.${index}.followupid`);

                          const confirmAndRemove = () => {
                            if (index > 0) {
                              setValue(`followups.${index - 1}.nextfollowuprequired`, "no");
                            }
                          };

                          if (fId) {
                            onDeleteFollowup?.(Number(fId), index, confirmAndRemove);
                          } else {
                            confirmAndRemove();
                          }
                        }}
                      />
                    )}
                    <i
                      className={isCollapsed ? "ri-arrow-down-s-line" : "ri-arrow-up-s-line"}
                      style={{ fontSize: "20px", color: "#8a96a3", cursor: "pointer" }}
                      onClick={() => {
                        setCollapsedMap((prev) => ({
                          ...prev,
                          [field.id]: !prev[field.id],
                        }));
                      }}
                    />
                  </div>
                </div>
                <Collapse isOpen={!isCollapsed}>
                  <div className={formStyles.sectionBody}>
                    <div
                      className="p-3 mb-2"
                      style={{
                        backgroundColor: "#fff",
                        position: "relative",
                      }}
                    >
                      {/* Row 1: Date, Start Time, End Time */}
                      <CommonForm
                        fields={followupFields.filter((f) =>
                          [
                            `followups.${index}.folloupdate`,
                            `followups.${index}.starttime`,
                            `followups.${index}.endtime`,
                          ].includes(f.name)
                        )}
                        control={control}
                        errors={flatErrors}
                        columns={3}
                        setValue={setValue}
                      />

                      {/* Row 2: Vital Signs */}
                      <div className="mt-3">
                        <CommonForm
                          fields={followupFields.filter((f) =>
                            [
                              `followups.${index}.bloodpressure`,
                              `followups.${index}.pulse`,
                              `followups.${index}.respiratoryrate`,
                              `followups.${index}.temperature`,
                              `followups.${index}.temperaturelocation`,
                              `followups.${index}.temperatureunit`,
                            ].includes(f.name)
                          )}
                          control={control}
                          errors={flatErrors}
                          columns={3}
                          setValue={setValue}
                        />
                      </div>                  {/* Row 3: Description & Is another follow up required */}
                      <div className="mt-3">
                        <CommonForm
                          fields={followupFields.filter((f) =>
                            [
                              `followups.${index}.description`,
                              `followups.${index}.nextfollowuprequired`
                            ].includes(f.name)
                          )}
                          control={control}
                          errors={flatErrors}
                          columns={2}
                          setValue={setValue}
                        />
                      </div>

                      {/* Card Action: Save Button */}
                      <div className="d-flex justify-content-end align-items-center mt-3">
                        <Button
                          type="button"
                          color={!isEditMode && isCardSaved ? "success" : "primary"}
                          outline
                          size="sm"
                          disabled={savingCardIndex === index || (!isEditMode && isCardSaved)}
                          onClick={async () => {
                            const isValid = await trigger(`followups.${index}`);
                            if (isValid) {
                              onSaveCard?.(index);
                            }
                          }}
                        >
                          {savingCardIndex === index ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Saving...
                            </>
                          ) : !isEditMode && isCardSaved ? (
                            <>
                              <i className="ri-check-line me-1" />
                              Saved
                            </>
                          ) : (
                            <>Save Follow-up</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Collapse>
              </div>
            );
          })}
        </>
      )}

      {/* Sticky Footer */}
      <div className={formStyles.stickyFooter}>
        <div className={formStyles.footerActions}>
          <div className="d-flex gap-2">
            <Button
              type="button"
              color="primary"
              outline
              onClick={onBack}
            >
              Back
            </Button>
          </div>
          <div className="d-flex gap-2">
            <Button
              type="button"
              color="primary"
              outline
              onClick={onNext}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AdverseEventFollowupsForm
